import React from "react";
import { Helmet } from "react-helmet-async";
import { useStore } from "../context/StoreContext";
import { Icons } from "../components/Icons";
import ProductCard from "../components/ProductCard";
import CategoryTile, { catGridStyle } from "../components/CategoryTile";
import Skeleton from "../components/Skeleton";
import Link from "../components/Link";
import { shopPath, slugify } from "../utils/slug";
import "./Shop.css";

/* The category a shopper lands on when they open the shop without
   picking anything ("/shop" with no slug). Matched against the live
   category names, so renaming "Necklaces" → "Necklace Sets" in the
   Admin Panel keeps working without a code change. */
const DEFAULT_CAT_SLUG = "necklace";

export default function Shop() {
  const {
    page, shopCat, setShopCat, shopSub, setShopSub, selectSubcat,
    shopSort, setShopSort,
    shopPriceMin, shopPriceMax, clearPriceFilter,
    getShopList,
    catNames, subcatsFor, categoryImage, catsLoading,
    products, productsLoading, subcatCount, catCount,
  } = useStore();

  const subcats = subcatsFor(shopCat);
  const hasSubcats = shopCat !== "All" && subcats.length > 0;

  /* ── Auto-select the default category on open ─────────────────
     When the shop page is opened on a bare "/shop" URL, land the
     shopper straight on the default category (Necklaces) instead of
     the empty category picker. This fires only when the page is
     *entered* — tapping the "All" chip while already in the shop
     still shows everything, and deep links like /shop/rings keep
     their own category. */
  const prevPage = React.useRef(null);
  const wantDefaultCat = React.useRef(false);

  React.useEffect(() => {
    const entered = page === "shop" && prevPage.current !== "shop";
    prevPage.current = page;
    if (!entered) return;
    // A deep link like /shop/rings carries its own category — leave it be.
    const parts = (window.location.pathname || "").split("/").filter(Boolean);
    if (parts[0] === "shop" && parts[1]) return;
    wantDefaultCat.current = true;
  }, [page]);

  React.useEffect(() => {
    if (!wantDefaultCat.current) return;
    if (shopCat !== "All") { wantDefaultCat.current = false; return; }
    if (!catNames.length) return; // categories still loading — try next render
    wantDefaultCat.current = false;
    const def =
      catNames.find((c) => slugify(c).includes(DEFAULT_CAT_SLUG)) || catNames[0];
    setShopCat(def);
    setShopSub("All");
    clearPriceFilter();
    // Keep the URL honest without adding a history entry, so the back
    // button still leaves the shop in one step.
    window.history.replaceState(
      { page: "shop", cat: def, sub: "All" },
      "",
      shopPath(def)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, catNames, shopCat]);

  /* Browsing model: Category → Products, with subcategories as optional
     filters. Entering a category shows everything in it right away
     ("Show all" is the default); tapping a subcategory chip narrows it. */
  const list = getShopList();

  const activePriceLabel =
    shopPriceMax === 1000 ? "Under Rs. 1,000" :
    (shopPriceMin === 1000 && shopPriceMax === 2000) ? "Rs. 1,000 – 2,000" :
    null;

  const pickCategory = (c) => {
    setShopCat(c);
    selectSubcat("All");
    clearPriceFilter();
  };

  /* Each category and subcategory URL needs its own title, description and
     self-referencing canonical, or they all collapse into one page as far
     as search engines are concerned. */
  const SITE = "https://cityshopingcenter.com";
  const seo = (() => {
    const canonical = SITE + shopPath(shopCat, shopSub);
    const crumbs = [{ "@type": "ListItem", position: 1, name: "Shop", item: SITE + "/shop" }];
    if (shopCat !== "All") {
      crumbs.push({ "@type": "ListItem", position: 2, name: shopCat, item: SITE + shopPath(shopCat) });
      if (shopSub !== "All") {
        crumbs.push({ "@type": "ListItem", position: 3, name: shopSub, item: canonical });
      }
    }
    const breadcrumb = { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: crumbs };

    if (shopCat === "All") {
      return {
        canonical, breadcrumb,
        title: "Buy Artificial Jewellery Online in Pakistan | Rings, Necklaces & Bridal Sets",
        description: "Shop artificial jewellery online in Pakistan — American Diamond, zircon & fancy rings, necklaces, earrings, bangles and bracelets. Bridal jewellery sets. Free delivery over Rs. 3,000.",
      };
    }
    if (shopSub !== "All") {
      return {
        canonical, breadcrumb,
        title: `${shopSub} — Buy ${shopCat} Online in Pakistan | City Shopping Center`,
        description: `Shop ${shopSub.toLowerCase()} online in Pakistan. American Diamond & zircon ${shopCat.toLowerCase()} delivered in 2–4 days. Free delivery over Rs. 3,000.`,
      };
    }
    return {
      canonical, breadcrumb,
      title: `Artificial ${shopCat} Online in Pakistan | City Shopping Center`,
      description: `Buy artificial ${shopCat.toLowerCase()} online in Pakistan — American Diamond & zircon designs. Delivered nationwide in 2–4 days. Free delivery over Rs. 3,000.`,
    };
  })();

  const headerLabel = activePriceLabel
    || (shopCat === "All" ? "JEWELLERY" : shopCat.toUpperCase());

  const headerSub = activePriceLabel
    ? `Showing items ${activePriceLabel}`
    : shopCat === "All"
      ? "American Diamond, zircon & bridal jewellery — delivered across Pakistan"
      : `${shopCat} — American Diamond & zircon collection`;

  /* Count of products behind a subcategory tile, so a shopper never
     taps into an empty shelf. */
  const countFor = (subName) => subcatCount(shopCat, subName);

  return (
    <div className="shop-page">
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <link rel="canonical" href={seo.canonical} />
        <script type="application/ld+json">{JSON.stringify(seo.breadcrumb)}</script>
      </Helmet>

      <div className="page-header" data-echo={shopCat === "All" ? "JEWELLERY" : shopCat.toUpperCase()}>
        <h1>{headerLabel}</h1>
        <p>{headerSub}</p>
      </div>

      <div className="shop-content">
        {/* ── Breadcrumb ─────────────────────────────────────── */}
        <nav className="shop-crumb" aria-label="Breadcrumb">
          <Link href="/shop" className="shop-crumb__link" onClick={() => pickCategory("All")}>Shop</Link>
          {shopCat !== "All" && (
            <>
              <span className="shop-crumb__sep" aria-hidden="true">/</span>
              <Link
                href={shopPath(shopCat)}
                className="shop-crumb__link"
                onClick={() => selectSubcat("All")}
              >
                {shopCat}
              </Link>
            </>
          )}
          {shopCat !== "All" && shopSub !== "All" && (
            <>
              <span className="shop-crumb__sep" aria-hidden="true">/</span>
              <span className="shop-crumb__current">{shopSub}</span>
            </>
          )}
        </nav>

        {/* ── Toolbar: categories + sort ─────────────────────── */}
        <div className="shop-toolbar">
          <div className="filter-group" role="group" aria-label="Filter by category">
            {catsLoading && catNames.length === 0 ? (
              <Skeleton.Chips count={5} />
            ) : (
              <>
                <Link
                  href="/shop"
                  className={`filter-btn${shopCat === "All" && !activePriceLabel ? " active" : ""}`}
                  onClick={() => pickCategory("All")}
                >
                  All
                </Link>
                {catNames.map((c) => (
                  <Link
                    key={c}
                    href={shopPath(c)}
                    className={`filter-btn${shopCat === c && !activePriceLabel ? " active" : ""}`}
                    onClick={() => pickCategory(c)}
                  >
                    {c}
                  </Link>
                ))}
              </>
            )}

            {activePriceLabel && (
              <span className="price-filter-badge">
                {activePriceLabel}
                <button className="price-filter-clear" onClick={clearPriceFilter} title="Clear price filter">
                  {Icons.close(10)}
                </button>
              </span>
            )}
          </div>

          <label className="sort-field">
            <span className="sort-field__label">Sort</span>
            <select
              className="sort-select"
              value={shopSort}
              onChange={(e) => setShopSort(e.target.value)}
            >
              <option value="def">Featured</option>
              <option value="pa">Price: Low → High</option>
              <option value="pd">Price: High → Low</option>
              <option value="az">Name A–Z</option>
            </select>
          </label>
        </div>

        {/* ── Level 1: pick a category ───────────────────────── */}
        {shopCat === "All" && catNames.length > 0 && (
          <section className="shop-level">
            <h2 className="shop-level__title">Browse by category</h2>
            <div className="cat-grid" style={catGridStyle(catNames.length)}>
              {catNames.map((c) => (
                <CategoryTile
                  key={c}
                  name={c}
                  image={categoryImage(c)}
                  meta={`${catCount(c)} ${catCount(c) === 1 ? "piece" : "pieces"}`}
                  href={shopPath(c)}
                  onClick={() => pickCategory(c)}
                />
              ))}
            </div>
          </section>
        )}

        {/* ── Level 2: pick a subcategory ────────────────────── */}
        {hasSubcats && (
          <section className="shop-level">
            <div className="subcat-bar">
              <h2 className="shop-level__title">
                {shopSub === "All" ? `Filter ${shopCat} by type` : `${shopCat} types`}
              </h2>
              <div className="subcat-chips" role="group" aria-label={`Filter ${shopCat} by type`}>
                {subcats.map((s) => (
                  <Link
                    key={s.id || s.name}
                    href={shopPath(shopCat, s.name)}
                    className={`subcat-chip${shopSub === s.name ? " active" : ""}`}
                    onClick={() => selectSubcat(s.name)}
                  >
                    {s.name}
                    <span className="subcat-chip__count">{countFor(s.name)}</span>
                  </Link>
                ))}
                <Link
                  href={shopPath(shopCat)}
                  className={`subcat-chip subcat-chip--all${shopSub === "All" ? " is-current" : ""}`}
                  onClick={() => selectSubcat("All")}
                >
                  Show all {shopCat}
                </Link>
              </div>
            </div>
          </section>
        )}

        {/* ── Level 3: products ──────────────────────────────── */}
        {productsLoading ? (
          <Skeleton.ProductGrid count={9} />
        ) : (
          <>
            <p className="products-count">
              {list.length === 0
                ? "No products to show"
                : `Showing ${list.length} product${list.length === 1 ? "" : "s"}`}
            </p>

            <div className="products-grid">
              {list.length === 0 ? (
                <div className="empty-state">
                  {Icons.diamond(40)}
                  <p>
                    {products.length === 0
                      ? "Products will appear here once they are added from the Admin Panel."
                      : `Nothing in ${shopSub !== "All" ? shopSub : shopCat === "All" ? "this selection" : shopCat} yet.`}
                  </p>
                  {products.length > 0 && (
                    <p className="empty-state__hint">Try another category or clear your filters.</p>
                  )}
                </div>
              ) : (
                list.map((p, pi) => (
                  <div key={p.id} data-reveal="up" style={{ transitionDelay: ((pi % 6) * 0.05) + "s" }}>
                    <ProductCard product={p} priority={pi < 3} />
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
